<template>
  <div class="relative" ref="dropdownRef">
    <!-- Select-like input -->
    <button
      type="button"
      @click="toggleDropdown"
      class="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm font-medium transition-colors"
      :class="isOpen
        ? 'ring-2 ring-primary-500 border-primary-500'
        : 'text-gray-700 hover:bg-gray-50'"
    >
      <span class="truncate">
        <template v-if="selectedOptions.length === 0">
          Select options...
        </template>
        <span v-else-if="selectedOptions.length === 1" :class="{ sensitive: sensitiveLabels }">{{ getOptionName(selectedOptions[0]) }}</span>
        <template v-else>
          {{ selectedOptions.length }} selected
        </template>
      </span>
      <font-awesome-icon
        :icon="['fas', 'chevron-down']"
        class="text-gray-500 text-xs ml-auto transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <!-- Teleported panel (preferred) -->
    <Teleport v-if="teleportToBody" to="body">
      <Transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <div
          v-if="isOpen"
          ref="panelRef"
          class="z-[10000] bg-white border border-gray-200 rounded-lg shadow-lg"
          :style="dropdownStyle"
        >
          <!-- Search input -->
          <div class="p-2 border-b border-gray-200">
            <input
              v-model="searchQuery"
              :class="{ sensitive: sensitiveLabels }"
              type="text"
              placeholder="Search..."
              class="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
              @click.stop
            />
          </div>

          <!-- Options list -->
          <div v-if="options.length > 0" class="max-h-48 overflow-y-auto">
            <label
              v-for="option in filteredPaginatedOptions"
              :key="option.id"
              class="flex items-center px-2 py-1 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="tempSelectedOptions.includes(option.id)"
                @change="toggleOption(option.id)"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-dark"
              />
              <span class="ml-2 text-sm text-gray-700 truncate ellipsis" :class="{ sensitive: sensitiveLabels }">{{ option.name }}</span>
            </label>
            <div v-if="filteredOptions.length === 0" class="px-3 py-2 text-sm text-gray-500 italic">
              No matching results
            </div>
          </div>
          <div v-else class="flex justify-center p-4">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>

          <!-- Action buttons -->
          <div class="border-t border-gray-200 bg-gray-50">
            <div class="flex gap-2 p-2">
              <button
                @click="selectAll"
                class="flex-1 px-2 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Select All
              </button>
              <button
                @click="selectNone"
                class="flex-1 px-2 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Clear
              </button>
            </div>

            <!-- Pagination -->
            <div v-if="shouldPaginate" class="flex items-center justify-between px-2 pb-2">
              <button
                :disabled="currentPage === 1"
                @click="prevPage"
                class="px-2 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ←
              </button>
              <span class="text-sm text-gray-600">
                {{ currentPage }} / {{ totalFilteredPages }}
              </span>
              <button
                :disabled="currentPage === totalFilteredPages"
                @click="nextPage"
                class="px-2 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                →
              </button>
            </div>

            <!-- Apply/Cancel buttons -->
            <div class="flex gap-2 p-2 pt-0">
              <button
                @click="applyValue"
                class="btn-primary-sm flex-1"
              >
                Apply
              </button>
              <button
                @click="cancel"
                class="flex-1 px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Fallback (no teleport) keeps original inline absolute behavior -->
    <Transition
      v-else
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-if="isOpen"
        ref="panelRef"
        class="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg"
        :style="dropdownStyle"
      >
        <!-- Search input -->
        <div class="p-2 border-b border-gray-200">
          <input
            v-model="searchQuery"
            :class="{ sensitive: sensitiveLabels }"
            type="text"
            placeholder="Search..."
            class="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
            @click.stop
          />
        </div>

        <!-- Options list -->
        <div v-if="options.length > 0" class="max-h-48 overflow-y-auto">
          <label
            v-for="option in filteredPaginatedOptions"
            :key="option.id"
            class="flex items-center px-2 py-1 hover:bg-gray-50 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="tempSelectedOptions.includes(option.id)"
              @change="toggleOption(option.id)"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-dark"
            />
            <span class="ml-2 text-sm text-gray-700 truncate ellipsis" :class="{ sensitive: sensitiveLabels }">{{ option.name }}</span>
          </label>
          <div v-if="filteredOptions.length === 0" class="px-3 py-2 text-sm text-gray-500 italic">
            No matching results
          </div>
        </div>
        <div v-else class="flex justify-center p-4">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>

        <!-- Action buttons -->
        <div class="border-t border-gray-200 bg-gray-50">
          <div class="flex gap-2 p-2">
            <button
              @click="selectAll"
              class="flex-1 px-2 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              Select All
            </button>
            <button
              @click="selectNone"
              class="flex-1 px-2 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              Clear
            </button>
          </div>

          <!-- Pagination -->
          <div v-if="shouldPaginate" class="flex items-center justify-between px-2 pb-2">
            <button
              :disabled="currentPage === 1"
              @click="prevPage"
              class="px-2 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ←
            </button>
            <span class="text-sm text-gray-600">
              {{ currentPage }} / {{ totalFilteredPages }}
            </span>
            <button
              :disabled="currentPage === totalFilteredPages"
              @click="nextPage"
              class="px-2 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              →
            </button>
          </div>

          <!-- Apply/Cancel buttons -->
          <div class="flex gap-2 p-2 pt-0">
            <button
              @click="applyValue"
              class="btn-primary-sm flex-1"
            >
              Apply
            </button>
            <button
              @click="cancel"
              class="flex-1 px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const props = defineProps({
  options: {
    type: Array,
    default: () => []
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Select options...'
  },
  teleportToBody: {
    type: Boolean,
    default: true
  },
  // With sensitiveLabels the options are identities (practices, providers,
  // staff): option labels and a single selected label are tagged, and the
  // search typed over them is emptied and read-only in Demo Mode.
  sensitiveLabels: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

// Refs
const dropdownRef = ref(null)
const panelRef = ref(null)
const isOpen = ref(false)
const searchQuery = ref('')
const currentPage = ref(1)
const selectedOptions = ref([...props.modelValue])
const tempSelectedOptions = ref([...props.modelValue])
const dropdownStyle = ref({ maxHeight: '400px', width: '250%' })

// Constants
const itemsPerPage = 20

// Computed
const filteredOptions = computed(() => {
  if (!searchQuery.value) {
    return props.options
  }

  const query = searchQuery.value.toLowerCase()
  return props.options.filter(option =>
    option.name.toLowerCase().includes(query)
  )
})

const filteredPaginatedOptions = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage
  return filteredOptions.value.slice(start, end)
})

const totalFilteredPages = computed(() => {
  return Math.ceil(filteredOptions.value.length / itemsPerPage)
})

const shouldPaginate = computed(() => {
  return filteredOptions.value.length > itemsPerPage
})

// Replace old computed dropdownStyle usage with this dynamic updater
const updatePosition = () => {
  if (!dropdownRef.value || !isOpen.value) return
  const rect = dropdownRef.value.getBoundingClientRect()
  const desiredWidthMultiplier = 1
  const computedWidth = Math.max(rect.width * desiredWidthMultiplier, rect.width)
  const panelMaxHeight = 400
  const spaceBelow = window.innerHeight - rect.bottom
  const spaceAbove = rect.top
  let top = rect.bottom
  let placement = 'bottom'
  if (spaceBelow < panelMaxHeight && spaceAbove > spaceBelow) {
    top = Math.max(8, rect.top - panelMaxHeight) // flip above
    placement = 'top'
  }
  dropdownStyle.value = props.teleportToBody
    ? {
        position: 'fixed',
        top: `${top}px`,
        left: `${rect.left}px`,
        width: `${computedWidth}px`,
        minWidth: '400px',
        maxHeight: `${panelMaxHeight}px`,
        overflow: 'auto',
        zIndex: 10000
      }
    : {
        maxHeight: '400px',
        width: '100%',
        [placement === 'top' ? 'bottom' : 'top']: placement === 'top' ? '100%' : 'auto'
      }
}

// Methods
const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    tempSelectedOptions.value = [...selectedOptions.value]
    nextTick(updatePosition)
  }
}

const closeDropdown = () => {
  isOpen.value = false
  searchQuery.value = ''
  currentPage.value = 1
}

const toggleOption = (optionId) => {
  const index = tempSelectedOptions.value.indexOf(optionId)
  if (index > -1) {
    tempSelectedOptions.value.splice(index, 1)
  } else {
    tempSelectedOptions.value.push(optionId)
  }
}

const applyValue = () => {
  selectedOptions.value = [...tempSelectedOptions.value]
  emit('update:modelValue', selectedOptions.value)
  emit('change', selectedOptions.value)
  closeDropdown()
}

const cancel = () => {
  tempSelectedOptions.value = [...selectedOptions.value]
  closeDropdown()
}

const selectAll = () => {
  tempSelectedOptions.value = filteredOptions.value.map(option => option.id)
}

const selectNone = () => {
  tempSelectedOptions.value = []
}

const nextPage = () => {
  if (currentPage.value < totalFilteredPages.value) {
    currentPage.value++
  }
}

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const getOptionName = (id) => {
  const option = props.options.find(opt => opt.id === id)
  return option ? option.name : ''
}

// Handle clicks outside
const handleClickOutside = (event) => {
  if (
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target) &&
    !(panelRef.value && panelRef.value.contains(event.target))
  ) {
    closeDropdown()
  }
}

// Watchers
watch(() => searchQuery.value, () => {
  currentPage.value = 1
})

watch(() => props.modelValue, (newValue) => {
  selectedOptions.value = [...newValue]
  tempSelectedOptions.value = [...newValue]
})

watch(isOpen, (val) => {
  if (val) {
    nextTick(updatePosition)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
  } else {
    window.removeEventListener('resize', updatePosition)
    window.removeEventListener('scroll', updatePosition, true)
  }
})

// Optional: reposition if options change (height may change)
watch(() => filteredOptions.value.length, () => {
  if (isOpen.value) nextTick(updatePosition)
})

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', updatePosition)
  window.removeEventListener('scroll', updatePosition, true)
})
</script>
